import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, RefreshCw, Save, Search, ShieldCheck, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getNavT, PAGE_PATH_TO_NAV_KEY } from '../../locales/navigation';
import { REPORT_SECTIONS } from '../Reports/reportRouteMeta';
import MainService from '../../services/MainService';

const NO_ACCESS_PAGE = '__NO_PAGE_ACCESS__';

const pageNameOverrides = {
  '/home/orders/reverse-cancel': 'Reverse Cancelled',
  '/home/expenses/driver-expense': 'Driver Expenses',
  '/home/expenses/company-expense': 'Company Expenses',
};

const sectionKeyMap = {
  home: 'home',
  masters: 'masters',
  accounts: 'accounts',
  orders: 'orders',
  messages: 'messages',
  uploads: 'uploads',
  delivery: 'delivery',
  shipments: 'shipments',
  payments: 'payments',
  followup: 'followup',
  expenses: 'expenses',
  approvals: 'approvals',
  tracking: 'tracking',
  vehicle: 'vehicleManagement',
  reports: 'reports',
  counters: 'counters',
  store: 'store',
  customization: 'customization',
  user: 'home',
};

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const isSuccess = (response) => response === true || response === 'true' || response === 'Success' || response?.Success === true || response?.success === true;

const getSectionLabel = (language, groupKey) => {
  const t = getNavT(language);
  const sectionKey = sectionKeyMap[groupKey] || groupKey;
  return t(`sections.${sectionKey}`);
};

const buildNavigationPages = (language) => {
  const tCurrent = getNavT(language);
  const tEnglish = getNavT('en');
  const pages = Object.entries(PAGE_PATH_TO_NAV_KEY).map(([path, navKey]) => {
    const groupKey = navKey.split('.')[0] || 'home';
    return {
      path,
      navKey,
      groupKey,
      groupLabel: getSectionLabel(language, groupKey),
      label: tCurrent(navKey),
      pageName: pageNameOverrides[path] || tEnglish(navKey),
    };
  });

  REPORT_SECTIONS.forEach((section) => {
    pages.push({
      path: `/home/reports/${section.id}`,
      navKey: `reports.${section.id}`,
      groupKey: 'reports',
      groupLabel: getSectionLabel(language, 'reports'),
      label: section.navLabel,
      pageName: section.navLabel,
    });

    section.cards.forEach((card) => {
      pages.push({
        path: `/home/reports/${card.slug}`,
        navKey: `reports.${card.slug}`,
        groupKey: 'reports',
        groupLabel: getSectionLabel(language, 'reports'),
        label: card.label,
        pageName: card.label,
      });
    });
  });

  const byPath = new Map();
  pages.forEach((page) => {
    if (!byPath.has(page.path)) byPath.set(page.path, page);
  });

  return Array.from(byPath.values()).sort((a, b) => {
    if (a.groupLabel !== b.groupLabel) return a.groupLabel.localeCompare(b.groupLabel);
    return a.label.localeCompare(b.label);
  });
};

const UserPageRole = () => {
  const { language } = useLanguage();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedPages, setSelectedPages] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const allPages = useMemo(() => buildNavigationPages(language), [language]);
  const sections = useMemo(() => {
    const seen = new Map();
    allPages.forEach((page) => {
      if (!seen.has(page.groupKey)) seen.set(page.groupKey, page.groupLabel);
    });
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label }));
  }, [allPages]);

  const filteredPages = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    return allPages.filter((page) => {
      const matchesSection = !sectionFilter || page.groupKey === sectionFilter;
      const matchesKeyword = !keyword ||
        page.label.toLowerCase().includes(keyword) ||
        page.pageName.toLowerCase().includes(keyword) ||
        page.path.toLowerCase().includes(keyword);
      return matchesSection && matchesKeyword;
    });
  }, [allPages, searchTerm, sectionFilter]);

  const groupedPages = useMemo(() => {
    const groups = new Map();
    filteredPages.forEach((page) => {
      if (!groups.has(page.groupKey)) {
        groups.set(page.groupKey, { key: page.groupKey, label: page.groupLabel, pages: [] });
      }
      groups.get(page.groupKey).pages.push(page);
    });
    return Array.from(groups.values());
  }, [filteredPages]);

  const selectedUser = users.find((user) => Number(user.Id) === Number(selectedUserId));
  const selectedCount = selectedPages.size;
  const visibleSelectedCount = filteredPages.filter((page) => selectedPages.has(page.pageName)).length;

  const loadUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      setError('');
      setUsers(parseList(await MainService.EmployeeUsersInDropDown()));
    } catch (err) {
      console.error('Load employee users error:', err);
      setError('Failed to load employee users');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadRoles = useCallback(async (userId) => {
    if (!userId) {
      setSelectedPages(new Set());
      return;
    }

    try {
      setLoadingRoles(true);
      setError('');
      const response = await MainService.GetEmployeePageRoleDetails(userId);
      const pages = parseList(response)
        .map((role) => role.PageName)
        .filter((pageName) => pageName && pageName !== NO_ACCESS_PAGE);
      setSelectedPages(new Set(pages));
    } catch (err) {
      console.error('Load user page roles error:', err);
      setSelectedPages(new Set());
      setError('Failed to load user page roles');
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadRoles(selectedUserId);
  }, [loadRoles, selectedUserId]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const togglePage = (pageName) => {
    setSelectedPages((prev) => {
      const next = new Set(prev);
      if (next.has(pageName)) next.delete(pageName);
      else next.add(pageName);
      return next;
    });
  };

  const selectVisible = () => {
    setSelectedPages((prev) => {
      const next = new Set(prev);
      filteredPages.forEach((page) => next.add(page.pageName));
      return next;
    });
  };

  const clearVisible = () => {
    setSelectedPages((prev) => {
      const next = new Set(prev);
      filteredPages.forEach((page) => next.delete(page.pageName));
      return next;
    });
  };

  const clearAll = () => setSelectedPages(new Set());

  const saveRoles = async () => {
    if (!selectedUserId) {
      setError('Select Employee User');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const pageNames = Array.from(selectedPages);
      const payload = pageNames.length > 0
        ? pageNames.map((pageName) => ({ UserId: Number(selectedUserId), PageName: pageName }))
        : [{ UserId: Number(selectedUserId), PageName: NO_ACCESS_PAGE }];
      const response = await MainService.AddUserPageRole(payload);
      if (isSuccess(response)) {
        setSuccess('Success');
        await loadRoles(selectedUserId);
      } else {
        setError(String(response || 'Failed to save page roles'));
      }
    } catch (err) {
      console.error('Save user page roles error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && <Toast type="error" message={error} onClose={() => setError('')} />}
      {success && <Toast type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-col gap-4 border-b border-gray-200 pb-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-3 text-primary">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">User Page Role</h1>
              <p className="mt-1 text-sm text-gray-500">Choose which pages an employee user can access.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={saveRoles}
            disabled={!selectedUserId || saving || loadingRoles}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            Save Roles
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Employee User</label>
            <select
              value={selectedUserId}
              onChange={(event) => setSelectedUserId(event.target.value)}
              disabled={loadingUsers}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
            >
              <option value="">{loadingUsers ? 'Loading users...' : 'Select Employee User'}</option>
              {users.map((user) => (
                <option key={user.Id} value={user.Id}>
                  {user.UserName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Section</label>
            <select
              value={sectionFilter}
              onChange={(event) => setSectionFilter(event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">All Sections</option>
              {sections.map((section) => (
                <option key={section.value} value={section.value}>
                  {section.label}
                </option>
              ))}
            </select>
          </div>

          <div className="lg:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">Search Pages</label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by page name, backend page name, or route"
                className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <span className="font-semibold text-gray-900">{selectedUser?.UserName || 'No user selected'}</span>
            <span className="ml-2">has {selectedCount} accessible page{selectedCount === 1 ? '' : 's'} selected.</span>
            {loadingRoles && <span className="ml-2 text-primary">Loading roles...</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={selectVisible} disabled={!selectedUserId || filteredPages.length === 0} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Select Visible
            </button>
            <button type="button" onClick={clearVisible} disabled={!selectedUserId || visibleSelectedCount === 0} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Clear Visible
            </button>
            <button type="button" onClick={clearAll} disabled={!selectedUserId || selectedCount === 0} className="rounded-lg border border-red-200 bg-white px-3 py-1.5 font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50">
              Clear All
            </button>
            <button type="button" onClick={() => loadRoles(selectedUserId)} disabled={!selectedUserId || loadingRoles} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${loadingRoles ? 'animate-spin' : ''}`} />
              Reload
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900">Accessible Pages</h2>
          <p className="text-sm text-gray-500">Showing {filteredPages.length} of {allPages.length} pages. Backend page names are saved for access checks.</p>
        </div>

        <div className="divide-y divide-gray-200">
          {groupedPages.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-gray-500">No pages found.</div>
          ) : (
            groupedPages.map((group) => (
              <section key={group.key} className="p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700">{group.label}</h3>
                  <span className="text-xs text-gray-500">
                    {group.pages.filter((page) => selectedPages.has(page.pageName)).length} / {group.pages.length} selected
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {group.pages.map((page) => {
                    const checked = selectedPages.has(page.pageName);
                    return (
                      <label
                        key={page.path}
                        className={`flex cursor-pointer gap-3 rounded-lg border p-3 transition-colors ${checked ? 'border-primary bg-primary/5' : 'border-gray-200 hover:bg-gray-50'}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!selectedUserId || loadingRoles}
                          onChange={() => togglePage(page.pageName)}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-gray-900">{page.label}</span>
                          <span className="mt-1 block truncate text-xs text-gray-500">{page.path}</span>
                          {page.pageName !== page.label && (
                            <span className="mt-1 block text-xs text-amber-700">Access name: {page.pageName}</span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const Toast = ({ type, message, onClose }) => {
  const isError = type === 'error';
  return (
    <div className={`fixed right-4 top-4 z-50 flex min-w-[280px] items-center gap-3 rounded-lg px-4 py-3 text-white shadow-lg ${isError ? 'bg-red-500' : 'bg-green-500'}`}>
      {isError ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button type="button" onClick={onClose} className="rounded p-1 hover:bg-white/20">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default UserPageRole;
